import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'danger-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. Sized automatically from `size`. */
  icon?: LucideIcon;
  /** Trailing icon (chevrons, external-link). */
  iconTrailing?: LucideIcon;
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

/** Shared with IconButton. Every variant is bordered (transparent where invisible)
 *  so bordered and borderless variants are the same box. */
// eslint-disable-next-line react-refresh/only-export-components -- shared style map, part of the ui API
export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-green-900 text-white border-transparent hover:bg-green-800 active:brightness-90',
  secondary: 'bg-cream-light text-green-900 border-border-strong hover:bg-cream-dark active:brightness-95',
  danger: 'bg-danger text-white border-transparent hover:brightness-95 active:brightness-90',
  ghost: 'bg-transparent text-green-900 border-transparent hover:bg-green-100 active:bg-green-200',
  'danger-ghost': 'bg-transparent text-danger border-transparent hover:bg-danger-soft active:brightness-95',
};

/** Shared with IconButton. Focus is owned by the global :focus-visible rule. */
export const BUTTON_BASE = cn(
  'inline-flex items-center justify-center shrink-0',
  'font-medium whitespace-nowrap select-none',
  'rounded-[var(--radius-control)] border',
  'transition-[background-color,border-color,color,filter]',
  'disabled:pointer-events-none disabled:bg-cream-dark disabled:text-ink-disabled disabled:border-transparent',
);

// Height is DECLARED from the control tokens; padding never determines it.
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-[var(--size-control-sm)] gap-1 px-3 text-xs', // 28px — dense table actions
  md: 'h-[var(--size-control-md)] gap-2 px-3 text-sm', // 32px — toolbar default
  lg: 'h-[var(--size-control-lg)] gap-2 px-4 text-sm', // 36px — forms, matches Input/Select
  xl: 'h-[var(--size-control-xl)] gap-2 px-5 text-base', // 44px — primary CTA / touch
};

const ICON_PX: Record<ButtonSize, number> = { sm: 12, md: 16, lg: 16, xl: 20 };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon: Lead,
    iconTrailing: Trail,
    loading = false,
    fullWidth,
    className,
    disabled,
    children,
    ...rest
  },
  ref,
) {
  const px = ICON_PX[size];
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(BUTTON_BASE, BUTTON_VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? (
        <Loader2 size={px} strokeWidth={2} className="animate-spin" aria-hidden="true" />
      ) : Lead ? (
        <Lead size={px} strokeWidth={2} aria-hidden="true" />
      ) : null}
      {children}
      {Trail && !loading ? <Trail size={px} strokeWidth={2} aria-hidden="true" /> : null}
    </button>
  );
});

export default Button;
